/**
 * Regras puras do elo Visitantes (sem React, sem rede) — um só lugar para
 * faixa etária, idade, telefone, estado de acompanhamento e links de contato.
 *
 * O banco continua sendo a fonte da verdade do que cada papel pode ver ou
 * gravar; aqui só há apresentação e conveniência (VIS-DEC-013, 022, 023).
 */

export type FaixaChave = "crianca" | "adolescente" | "jovem" | "adulto" | "idoso";

export interface Faixa {
  chave: FaixaChave;
  rotulo: string;
  plural: string;
  /** Idade mínima (inclusive). */
  min: number;
  /** Idade máxima (inclusive); `null` = sem limite. */
  max: number | null;
  /** Classes de cor (tokens semânticos — funcionam nos dois temas). */
  classe: string;
}

/** Faixas etárias do elo (proposta aprovada): calculadas pela data de nascimento, nunca digitadas. */
export const FAIXAS: Faixa[] = [
  {
    chave: "crianca",
    rotulo: "Criança",
    plural: "Crianças",
    min: 0,
    max: 11,
    classe: "bg-warning/15 text-warning",
  },
  {
    chave: "adolescente",
    rotulo: "Adolescente",
    plural: "Adolescentes",
    min: 12,
    max: 17,
    classe: "bg-info/15 text-info",
  },
  {
    chave: "jovem",
    rotulo: "Jovem",
    plural: "Jovens",
    min: 18,
    max: 29,
    classe: "bg-success/15 text-success",
  },
  {
    chave: "adulto",
    rotulo: "Adulto",
    plural: "Adultos",
    min: 30,
    max: 69,
    classe: "bg-primary/15 text-primary",
  },
  {
    chave: "idoso",
    rotulo: "Idoso",
    plural: "Idosos",
    min: 70,
    max: null,
    classe: "bg-surface-2 text-muted",
  },
];

/** Abaixo disso o visitante é criança: exige responsável e só o ministério responsável o enxerga. */
export const IDADE_CRIANCA_ATE = 11;

export function faixaDaIdade(idade: number): Faixa {
  return (
    FAIXAS.find((f) => idade >= f.min && (f.max === null || idade <= f.max)) ??
    (FAIXAS[FAIXAS.length - 1] as Faixa)
  );
}

const PAD = (n: number) => String(n).padStart(2, "0");

/** Data de hoje ("AAAA-MM-DD") no fuso de São Paulo — o mesmo critério do banco (`_visitante_hoje`). */
export function hojeSaoPaulo(agora: Date = new Date()): string {
  const partes = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(agora);
  return partes; // en-CA => AAAA-MM-DD
}

/** "AAAA-MM-DD" -> [ano, mês, dia] numéricos. */
function partesDaData(iso: string): [number, number, number] {
  const [a = "0", m = "0", d = "0"] = iso.split("-");
  return [Number(a), Number(m), Number(d)];
}

/** Soma `dias` a uma data "AAAA-MM-DD" (aritmética em UTC, sem surpresa de fuso). */
export function somarDias(iso: string, dias: number): string {
  const [a, m, d] = partesDaData(iso);
  const dt = new Date(Date.UTC(a, m - 1, d + dias));
  return `${dt.getUTCFullYear()}-${PAD(dt.getUTCMonth() + 1)}-${PAD(dt.getUTCDate())}`;
}

/** Idade completa em anos na data `hoje`; `null` sem data de nascimento válida. */
export function calcularIdade(
  nascimento: string | null | undefined,
  hoje: string = hojeSaoPaulo(),
): number | null {
  if (!nascimento || !/^\d{4}-\d{2}-\d{2}$/.test(nascimento)) return null;
  const [na, nm, nd] = partesDaData(nascimento);
  const [ha, hm, hd] = partesDaData(hoje);
  let idade = ha - na;
  if (hm < nm || (hm === nm && hd < nd)) idade -= 1;
  return idade >= 0 ? idade : null;
}

/** "AAAA-MM-DD" -> "DD/MM/AAAA" (sem passar por `Date`, para não deslocar o dia). */
export function formatarData(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${a}`;
}

export function soDigitos(valor: string): string {
  return valor.replace(/\D/g, "");
}

/** "11987654321" -> "(11) 98765-4321"; devolve o texto como veio se não tiver 10/11 dígitos. */
export function formatarTelefone(valor: string | null | undefined): string {
  if (!valor) return "—";
  const d = soDigitos(valor);
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return valor;
}

/** Máscara de digitação do campo de telefone (aceita 10 ou 11 dígitos). */
export function mascararTelefoneDigitando(valor: string): string {
  const d = soDigitos(valor).slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export const telefoneValido = (valor: string) => /^[0-9]{10,11}$/.test(soDigitos(valor));

export function linkLigar(telefone: string): string {
  return `tel:+55${soDigitos(telefone)}`;
}

export function linkWhatsapp(telefone: string, texto: string): string {
  return `https://wa.me/55${soDigitos(telefone)}?text=${encodeURIComponent(texto)}`;
}

/** Estado civil (opcional, a partir dos 12 anos): chave guardada no banco e rótulo na tela. */
export type EstadoCivil = "solteiro" | "casado" | "uniao_estavel" | "divorciado" | "viuvo";

export const ESTADOS_CIVIS: { chave: EstadoCivil; rotulo: string }[] = [
  { chave: "solteiro", rotulo: "Solteiro(a)" },
  { chave: "casado", rotulo: "Casado(a)" },
  { chave: "uniao_estavel", rotulo: "União estável" },
  { chave: "divorciado", rotulo: "Divorciado(a)" },
  { chave: "viuvo", rotulo: "Viúvo(a)" },
];

/** Rótulo do estado civil; devolve `null` quando não informado ou desconhecido. */
export function rotuloEstadoCivil(chave: string | null | undefined): string | null {
  return ESTADOS_CIVIS.find((e) => e.chave === chave)?.rotulo ?? null;
}

/** Aceita só uma chave válida (o resto vira "não informado"). */
export function soEstadoCivil(v: string | null | undefined): EstadoCivil | "" {
  return ESTADOS_CIVIS.find((e) => e.chave === v)?.chave ?? "";
}

/** Abre o WhatsApp sem destinatário: quem compartilha escolhe o líder dentro do próprio WhatsApp. */
export function linkWhatsappCompartilhar(texto: string): string {
  return `https://wa.me/?text=${encodeURIComponent(texto)}`;
}

/** O que a mensagem de "Compartilhar contato" pode levar: tudo que foi preenchido e quem compartilha enxerga. */
export interface DadosCompartilhar {
  nome: string;
  idade: number | null;
  nascimento?: string | null;
  telefone?: string | null;
  email?: string | null;
  estadoCivil?: string | null;
  respNome?: string | null;
  respTelefone?: string | null;
  igrejaSituacao?: string | null;
  igrejaNome?: string | null;
  cidade?: string | null;
  bairro?: string | null;
  endereco?: string | null;
  preferenciaContato?: string | null;
  pedidoOracao?: string | null;
  observacao?: string | null;
  /** Datas das visitas, da mais recente para a mais antiga. */
  visitas?: string[];
  /** Quem veio junto (já formatado, ex.: "Ana (cônjuge)"). */
  veioCom?: string[];
}

/**
 * Mensagem para passar o contato de um visitante a outro líder. Leva **todas as informações
 * preenchidas** que quem compartilha enxerga (a ficha já entrega só o que o papel pode ver) e
 * lembra que serve só para o acompanhamento na igreja. Quem envia revisa e pode apagar linhas.
 */
export function textoCompartilharContato(v: DadosCompartilhar): string {
  const vazio = (x: string | null | undefined) => !x || x.trim() === "";
  const faixa = v.idade !== null ? ` (${faixaDaIdade(v.idade).rotulo}, ${v.idade} anos)` : "";
  const linhas = [`Visitante da igreja: ${v.nome}${faixa}`];
  const add = (rotulo: string, valor: string | null | undefined) => {
    if (!vazio(valor)) linhas.push(`${rotulo}: ${valor}`);
  };
  add("Telefone", vazio(v.telefone) ? null : formatarTelefone(v.telefone));
  add("Nascimento", vazio(v.nascimento) ? null : formatarData(v.nascimento));
  add("E-mail", v.email);
  add("Estado civil", rotuloEstadoCivil(v.estadoCivil));
  if (!vazio(v.respNome) || !vazio(v.respTelefone)) {
    const tel = vazio(v.respTelefone) ? "" : ` — ${formatarTelefone(v.respTelefone)}`;
    linhas.push(`Responsável: ${vazio(v.respNome) ? "(sem nome)" : v.respNome}${tel}`);
  }
  if (v.igrejaSituacao === "outra") {
    add("Frequenta outra igreja", vazio(v.igrejaNome) ? "sim" : `sim, ${v.igrejaNome}`);
  } else if (v.igrejaSituacao === "sem") {
    add("Frequenta outra igreja", "não, não frequenta nenhuma");
  }
  add("Cidade", v.cidade);
  add("Bairro", v.bairro);
  add("Rua e número", v.endereco);
  const pref = ROTULO_PREFERENCIA[v.preferenciaContato as PreferenciaContato];
  add("Prefere ser procurado(a)", pref ?? null);
  add("Pedido de oração", v.pedidoOracao);
  add("Observação", v.observacao);
  const visitas = v.visitas ?? [];
  if (visitas.length > 0) {
    linhas.push(
      `Visitas: ${visitas.length}${visitas[0] ? ` (última em ${formatarData(visitas[0])})` : ""}`,
    );
  }
  if (v.veioCom && v.veioCom.length > 0) linhas.push(`Veio com: ${v.veioCom.join(", ")}`);
  linhas.push("Contato só para cuidar da pessoa na igreja. Por favor, não repasse.");
  return linhas.join("\n");
}

/** Modelos de mensagem do WhatsApp (editáveis antes de abrir). */
export const MODELOS_WHATSAPP = [
  {
    chave: "boas_vindas",
    rotulo: "Boas-vindas",
    texto: (nome: string) =>
      `Olá, ${nome}! Foi uma alegria receber você na nossa igreja. Esperamos ver você de novo em breve!`,
  },
  {
    chave: "convite",
    rotulo: "Convite",
    texto: (nome: string) =>
      `Olá, ${nome}! Gostaríamos de convidar você para o nosso próximo culto. Será uma alegria ter você conosco!`,
  },
  {
    chave: "recorrente",
    rotulo: "Visitante recorrente",
    texto: (nome: string) =>
      `Olá, ${nome}! Que bom ver você de novo por aqui. Se precisar de qualquer coisa, estamos à disposição.`,
  },
] as const;

export type CanalContato = "whatsapp" | "telefone" | "pessoalmente" | "outro";

export const CANAIS_CONTATO: { chave: CanalContato; rotulo: string }[] = [
  { chave: "whatsapp", rotulo: "WhatsApp" },
  { chave: "telefone", rotulo: "Ligação" },
  { chave: "pessoalmente", rotulo: "Pessoalmente" },
  { chave: "outro", rotulo: "Outro" },
];

export const rotuloCanal = (c: string) => CANAIS_CONTATO.find((x) => x.chave === c)?.rotulo ?? c;

export type PreferenciaContato = "visita" | "contato" | "nao";

export const ROTULO_PREFERENCIA: Record<PreferenciaContato, string> = {
  visita: "Aceita visita em casa",
  contato: "Aceita contato (mensagem ou ligação)",
  nao: "Prefere não ser procurado(a)",
};

export type EstadoAcompanhamento = "nao_contatado" | "contatado" | "precisa";

export const ROTULO_ESTADO: Record<EstadoAcompanhamento, string> = {
  nao_contatado: "Não contatado",
  contatado: "Contatado",
  precisa: "Precisa de acompanhamento",
};

export const CLASSE_ESTADO: Record<EstadoAcompanhamento, string> = {
  nao_contatado: "bg-surface-2 text-muted",
  contatado: "bg-success/15 text-success",
  precisa: "bg-danger/10 text-danger",
};

/** Visitas sem nenhum contato a partir das quais a pessoa "precisa de acompanhamento". */
export const VISITAS_PARA_ACOMPANHAR = 3;

/**
 * Estado de acompanhamento (VIS-DEC-023): quem pediu para não ser procurado(a) nunca
 * entra em "Precisa de acompanhamento".
 */
export function estadoDe(v: {
  contatos: number;
  visitas: number;
  preferencia_contato: string | null;
}): EstadoAcompanhamento {
  if (v.contatos > 0) return "contatado";
  if (v.visitas >= VISITAS_PARA_ACOMPANHAR && v.preferencia_contato !== "nao") return "precisa";
  return "nao_contatado";
}

/**
 * Criança (até 11 anos) sem contato registrado fica fora do acompanhamento: quem é
 * procurado é o responsável. Ela não ganha selo "Não contatado"/"Precisa de acompanhamento"
 * e não aparece nesses filtros nem no aviso da lista.
 */
export function foraDoAcompanhamento(
  v: { nascimento: string | null; contatos: number },
  hoje: string = hojeSaoPaulo(),
): boolean {
  const idade = calcularIdade(v.nascimento, hoje);
  return idade !== null && idade <= IDADE_CRIANCA_ATE && v.contatos === 0;
}

/** Contatado que visitou de novo depois do último contato (VIS-DEC-008): ganha o selo "Voltou". */
export function voltouAposContato(v: {
  contatos: number;
  ultima_visita: string | null;
  ultimo_contato: string | null;
}): boolean {
  return (
    v.contatos > 0 && !!v.ultima_visita && !!v.ultimo_contato && v.ultima_visita > v.ultimo_contato
  );
}

/** Para quem ligar/escrever: criança (<12) -> responsável; senão a própria pessoa. */
export function alvoDeContato(v: {
  nascimento: string | null;
  telefone: string | null;
  resp_nome?: string | null;
  resp_telefone?: string | null;
}): { telefone: string | null; nome: string | null; ehResponsavel: boolean } {
  const idade = calcularIdade(v.nascimento);
  if (idade !== null && idade <= IDADE_CRIANCA_ATE) {
    return { telefone: v.resp_telefone ?? null, nome: v.resp_nome ?? null, ehResponsavel: true };
  }
  return { telefone: v.telefone, nome: null, ehResponsavel: false };
}

/** Nome normalizado para detectar cadastro repetido (sem acento, sem caixa, espaços únicos). */
export function normalizarNome(nome: string): string {
  return nome.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, " ").trim();
}

export function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome;
}
